/**
 * The four address lines three forms ask for — a provider's base (`W2-T05`), a job's place
 * (`W4-T11`) and an address-book entry (`W2-T04`) — read and checked in one place.
 *
 * All send `ProviderAddressCreateSchema`'s shape: the lines, no point, because the server places the
 * postcode (`MEM-2026-09-28-identity-2`). The rules restate that schema's bounds so a person is told
 * which line is wrong before anything is sent. Errors are translation keys under the calling form's
 * own prefix (`proForm.error.line1`, `jobForm.error.line1`), so each form keeps its own wording.
 */
import { type ProviderAddressCreate } from '@marketplace/contracts';

export type AddressField = 'line1' | 'postalCode' | 'city' | 'province';

export interface AddressRead {
  /** Built whether or not it is valid; the caller sends it only when `errors` is empty. */
  address: ProviderAddressCreate;
  errors: Partial<Record<AddressField, string>>;
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function readAddress(
  form: FormData,
  prefix: 'proForm' | 'jobForm' | 'account.addresses',
): AddressRead {
  const errors: Partial<Record<AddressField, string>> = {};

  const line1 = text(form, 'line1');
  const postalCode = text(form, 'postalCode');
  const city = text(form, 'city');
  const province = text(form, 'province');

  if (line1 === '') errors.line1 = `${prefix}.error.line1`;
  else if (line1.length > 200) errors.line1 = `${prefix}.error.tooLong`;
  if (!/^[0-9]{5}$/.test(postalCode)) errors.postalCode = `${prefix}.error.postalCode`;
  if (city === '') errors.city = `${prefix}.error.city`;
  else if (city.length > 100) errors.city = `${prefix}.error.tooLong`;
  if (province === '') errors.province = `${prefix}.error.province`;
  else if (province.length > 100) errors.province = `${prefix}.error.tooLong`;

  return {
    address: { label: null, line1, line2: null, city, province, postalCode },
    errors,
  };
}
